import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Logo } from "../../components/Logo";
import { LANG_LABEL } from "../../lib/categories";
import { ArrowLeft, Plus, Save, Trash2, X } from "lucide-react";

const EMPTY = { title: "", url: "", description: "", category: "Recherche scientifique", language: "fr", source_name: "", order: 999 };

export default function AdminResources() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // id or "new"
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");

  useEffect(() => { if (user === false) navigate("/admin/login"); }, [user, navigate]);

  const load = () => {
    setLoading(true);
    api.get("/admin/resources").then((r) => setResources(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { if (user && (user.role === "admin" || user.role === "editor")) load(); }, [user]);

  const startNew = () => { setEditing("new"); setForm(EMPTY); setError(""); };
  const startEdit = (r) => { setEditing(r.id); setForm({ ...EMPTY, ...r }); setError(""); };
  const cancel = () => { setEditing(null); setError(""); };

  const save = async () => {
    setError("");
    try {
      if (editing === "new") await api.post("/admin/resources", form);
      else await api.put(`/admin/resources/${editing}`, form);
      setEditing(null);
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Sauvegarde impossible");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette ressource ?")) return;
    await api.delete(`/admin/resources/${id}`);
    load();
  };

  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // group by category for display
  const byCat = {};
  resources.forEach((r) => { (byCat[r.category] = byCat[r.category] || []).push(r); });

  if (!user || user === null) return <div className="py-32 text-center font-mono-label">CHARGEMENT…</div>;

  return (
    <div className="min-h-screen bg-[#FAF8F3]" data-testid="admin-resources">
      <header className="border-b-2 border-[#1A1A18] bg-[#FAF8F3] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="font-mono-label flex items-center gap-1 hover:text-[#4A7A3D]" data-testid="resources-back"><ArrowLeft size={14} /> Retour</Link>
            <Logo size="sm" />
          </div>
          <button onClick={startNew} className="bg-[#4A7A3D] text-[#FAF8F3] px-4 py-2 font-mono-label hover:bg-[#3E6633] flex items-center gap-2" data-testid="resources-new"><Plus size={16} /> Nouvelle ressource</button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8">
        <div>
          <div className="font-mono-label text-[#4A7A3D] mb-2">Gestion</div>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.0]">Ressources</h1>
          <p className="text-[#4B4B48] mt-3">{resources.length} ressources actives, classées par catégorie.</p>
        </div>

        {editing === "new" && (
          <ResourceForm form={form} u={u} onSave={save} onCancel={cancel} error={error} />
        )}

        {loading ? <p className="font-mono-label">CHARGEMENT…</p> : (
          <div className="space-y-10">
            {Object.keys(byCat).sort().map((cat) => (
              <section key={cat}>
                <div className="font-mono-label text-[#4A7A3D] mb-3 pb-2 border-b-2 border-[#1A1A18]">{cat} ({byCat[cat].length})</div>
                <div className="space-y-2">
                  {byCat[cat].map((r) => (
                    <div key={r.id} className="border border-[#E0D8CE]" data-testid={`resource-row-${r.id}`}>
                      {editing === r.id ? (
                        <div className="p-4 bg-[#F5EFEB]"><ResourceForm form={form} u={u} onSave={save} onCancel={cancel} error={error} /></div>
                      ) : (
                        <div className="flex flex-wrap gap-3 p-4 items-start">
                          <div className="flex-1 min-w-[240px]">
                            <div className="font-display text-lg leading-tight">{r.title}</div>
                            <div className="font-mono-label text-[#4A7A3D] text-[10px] mt-1">{r.source_name} · {LANG_LABEL[r.language]}</div>
                            <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#4B4B48] underline truncate block max-w-md">{r.url}</a>
                          </div>
                          <button onClick={() => startEdit(r)} className="border-2 border-[#1A1A18] px-3 py-1.5 font-mono-label hover:bg-[#1A1A18] hover:text-[#FAF8F3]" data-testid={`resource-edit-${r.id}`}>Éditer</button>
                          <button onClick={() => del(r.id)} className="border-2 border-red-700 text-red-700 px-3 py-1.5 font-mono-label hover:bg-red-700 hover:text-white" data-testid={`resource-delete-${r.id}`}><Trash2 size={14} /></button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function ResourceForm({ form, u, onSave, onCancel, error }) {
  return (
    <div className="space-y-4 p-4 border-2 border-[#1A1A18] bg-[#F5EFEB]">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="font-mono-label text-[#4B4B48] block mb-1">Titre</label>
          <input value={form.title} onChange={(e) => u("title", e.target.value)} className="bg-transparent border-b-2 border-[#1A1A18] w-full py-2 font-display text-xl outline-none" data-testid="resource-form-title" />
        </div>
        <div>
          <label className="font-mono-label text-[#4B4B48] block mb-1">Catégorie / thématique</label>
          <input value={form.category} onChange={(e) => u("category", e.target.value)} list="resource-categories" placeholder="Ex: Recherche scientifique" className="bg-transparent border-b-2 border-[#1A1A18] w-full py-2 outline-none" data-testid="resource-form-category" />
          <datalist id="resource-categories">
            <option value="Recherche scientifique" />
            <option value="Cannabis médical" />
            <option value="Politiques publiques" />
            <option value="Médias spécialisés" />
            <option value="Maurice & océan Indien" />
            <option value="Réduction des risques" />
            <option value="Histoire & culture" />
          </datalist>
        </div>
      </div>
      <div>
        <label className="font-mono-label text-[#4B4B48] block mb-1">URL externe</label>
        <input type="url" value={form.url} onChange={(e) => u("url", e.target.value)} placeholder="https://…" className="bg-transparent border-b-2 border-[#1A1A18] w-full py-2 font-mono outline-none" data-testid="resource-form-url" />
      </div>
      <div>
        <label className="font-mono-label text-[#4B4B48] block mb-1">Description</label>
        <textarea rows={2} value={form.description} onChange={(e) => u("description", e.target.value)} className="bg-transparent border-2 border-[#E0D8CE] w-full p-2 outline-none focus:border-[#4A7A3D]" data-testid="resource-form-description" />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <label className="font-mono-label text-[#4B4B48] block mb-1">Source / Organisme</label>
          <input value={form.source_name} onChange={(e) => u("source_name", e.target.value)} placeholder="OMS, Lancet, Newsweed…" className="bg-transparent border-b-2 border-[#1A1A18] w-full py-2 outline-none" data-testid="resource-form-source" />
        </div>
        <div>
          <label className="font-mono-label text-[#4B4B48] block mb-1">Langue</label>
          <select value={form.language} onChange={(e) => u("language", e.target.value)} className="bg-transparent border-2 border-[#1A1A18] w-full p-2 font-mono-label" data-testid="resource-form-language">
            {Object.entries(LANG_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div>
          <label className="font-mono-label text-[#4B4B48] block mb-1">Ordre</label>
          <input type="number" value={form.order} onChange={(e) => u("order", parseInt(e.target.value) || 0)} className="bg-transparent border-b-2 border-[#1A1A18] w-full py-2 outline-none" data-testid="resource-form-order" />
        </div>
      </div>
      <div className="flex flex-wrap gap-3 justify-end">
        {error && <div className="text-red-700 font-mono-label flex-1">{error}</div>}
        <button onClick={onCancel} className="border-2 border-[#1A1A18] px-4 py-2 font-mono-label flex items-center gap-2" data-testid="resource-form-cancel"><X size={14} /> Annuler</button>
        <button onClick={onSave} className="bg-[#4A7A3D] text-[#FAF8F3] px-4 py-2 font-mono-label flex items-center gap-2 hover:bg-[#3E6633]" data-testid="resource-form-save"><Save size={14} /> Sauvegarder</button>
      </div>
    </div>
  );
}
